/**
 * ORDAN design tokens (Brand Guidelines v1.0).
 *
 * Property names such as `commandBlue` are kept for API stability; hex values
 * map to the ORDAN teal / neutral palette.
 */

export const NobleTokens = {
  /**
   * Color System — ORDAN enterprise palette
   */
  colors: {
    // Primary Action Colors (keys retained; values = ORDAN Teal scale)
    primary: {
      commandBlue: '#145A54',
      commandBlueLight: '#1A6B63',
      commandBlueDark: '#0F4540',
      commandBlueUltraDark: '#0B1720',
    },
    
    // Accent Colors
    accent: {
      safetyOrange: '#FF6B2C',
      safetyOrangeLight: '#FF8A57',
      safetyOrangeDark: '#D94E15',
      
      precisionTeal: '#36C7BE',
      precisionTealLight: '#5ED4CD',
      precisionTealDark: '#2A9E97',
      
      steelGray: '#66727A',
      steelGrayLight: '#8A949B',
      steelGrayDark: '#4A545B',
      steelGrayUltraDark: '#101820',
    },
    
    // Semantic Status Colors
    semantic: {
      criticalRed: '#E63946',
      criticalRedLight: '#FF4D5A',
      criticalRedDark: '#C62630',
      
      warningAmber: '#F77F00',
      warningAmberLight: '#FF9B2E',
      warningAmberDark: '#D66A00',
      
      operationalGreen: '#06D6A0',
      operationalGreenLight: '#1FFFCB',
      operationalGreenDark: '#05B589',
      
      infoBlue: '#36C7BE',
      infoBluLight: '#5ED4CD',
      infoBlueDark: '#2A9E97',
      
      maintenanceYellow: '#FFB703',
      maintenanceYellowLight: '#FFD65C',
      maintenanceYellowDark: '#E5A500',
      
      inspectionPurple: '#6A4C93',
      inspectionPurpleLight: '#8B6DB8',
      inspectionPurpleDark: '#543A74',
    },
    
    // Background System (Depth Levels)
    background: {
      // Light Mode — Warm White / Soft Grey
      level0Light: '#F7F8F7',
      level1Light: '#FFFFFF',
      level2Light: '#FFFFFF',
      level3Light: '#F7F8F7',
      level4Light: '#E8ECEB',
      
      // Dark Mode — Deep Midnight / Obsidian
      level0Dark: '#0B1720',
      level1Dark: '#101820',
      level2Dark: '#1A2430',
      level3Dark: '#243040',
      level4Dark: '#2E3A4A',
    },
    
    // Text Colors
    text: {
      primary: 'rgba(16, 24, 32, 0.92)',
      secondary: 'rgba(102, 114, 122, 1)',
      disabled: 'rgba(102, 114, 122, 0.45)',
      hint: 'rgba(102, 114, 122, 0.55)',
      
      // Dark mode
      primaryDark: 'rgba(255, 255, 255, 0.95)',
      secondaryDark: 'rgba(232, 236, 235, 0.75)',
      disabledDark: 'rgba(255, 255, 255, 0.38)',
    },
    
    // Border Colors
    border: {
      default: '#E8ECEB',
      light: '#F7F8F7',
      dark: '#D1D5DB',
      
      // For etched/emboss effects
      etched: 'rgba(102, 114, 122, 0.15)',
      highlight: 'rgba(255, 255, 255, 0.8)',
    },
    
    // Restrained gradients (no decorative glow)
    gradients: {
      command: 'linear-gradient(135deg, #145A54 0%, #0F4540 100%)',
      steel: 'linear-gradient(135deg, #66727A 0%, #4A545B 100%)',
      alert: 'linear-gradient(135deg, #FF6B2C 0%, #D94E15 100%)',
      success: 'linear-gradient(135deg, #36C7BE 0%, #2A9E97 100%)',
      
      surface: 'linear-gradient(145deg, #FFFFFF, #F7F8F7)',
      surfaceDark: 'linear-gradient(145deg, #101820, #0B1720)',
    },
  },
  
  /**
   * Typography System
   */
  typography: {
    fontFamily: {
      primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', sans-serif",
      mono: "'JetBrains Mono', 'SF Mono', 'Consolas', 'Monaco', monospace",
      display: "'Syne', 'Segoe UI', system-ui, sans-serif",
    },
    
    // Type Scale (Strict: 12|14|16|20|24|32|48)
    fontSize: {
      display: '48px',
      h1Desktop: '32px',
      h1Mobile: '26px',
      h2Desktop: '24px',
      h2Mobile: '20px',
      h3Desktop: '20px',
      h3Mobile: '18px',
      h4Desktop: '16px',
      h4Mobile: '15px',
      body: '15px',
      bodyMobile: '14px',
      bodySmall: '13px',
      bodySmallMobile: '12px',
      caption: '11px',
      captionMobile: '10px',
      mono: '14px',
      monoMobile: '13px',
    },
    
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.6,
      loose: 1.75,
    },
    
    letterSpacing: {
      tight: '-0.02em',
      normal: '0',
      wide: '0.05em',
    },
  },
  
  /**
   * Spacing System (8px base unit)
   */
  spacing: {
    micro: '4px',    // 0.5x
    xs: '8px',       // 1x
    sm: '12px',      // 1.5x
    md: '16px',      // 2x
    lg: '24px',      // 3x
    xl: '32px',      // 4x
    xxl: '48px',     // 6x
    xxxl: '64px',    // 8x
    huge: '96px',    // 12x
  },
  
  /**
   * Border Radius
   */
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  
  /**
   * Shadows & Depth (Industrial Style)
   * Soft shadows with subtle blue tint
   */
  shadows: {
    // Elevation levels
    level0: 'none',
    level1: '0 2px 8px rgba(0, 0, 0, 0.06)',
    level2: '0 4px 16px rgba(0, 0, 0, 0.08)',
    level3: '0 12px 48px rgba(0, 0, 0, 0.15)',
    level4: '0 16px 64px rgba(0, 0, 0, 0.2)',
    
    // Neumorphic effects
    neumorphicRaised: '0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    neumorphicInset: 'inset 0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 0 rgba(255, 255, 255, 0.8)',
    
    // Focus states
    focusBlue: '0 0 0 4px rgba(20, 90, 84, 0.12)',
    focusTeal: '0 0 0 4px rgba(54, 199, 190, 0.15)',
    focusRed: '0 0 0 4px rgba(230, 57, 70, 0.1)',
    
    // Button effects
    buttonHover: '0 6px 16px rgba(20, 90, 84, 0.28), inset 0 -2px 4px rgba(0, 0, 0, 0.1)',
    buttonActive: '0 2px 6px rgba(20, 90, 84, 0.22), inset 0 2px 4px rgba(0, 0, 0, 0.15)',
  },
  
  /**
   * Z-Index Scale
   */
  zIndex: {
    base: 0,
    dropdown: 1050,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
    notification: 1080,
    blade: 1055,
    commandBar: 1025,
  },
  
  /**
   * Animation Timing
   */
  animation: {
    duration: {
      instant: '0ms',
      fast: '150ms',
      standard: '250ms',
      smooth: '350ms',
      slow: '500ms',
    },
    
    easing: {
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
  },
  
  /**
   * Breakpoints (Responsive Design)
   */
  breakpoints: {
    mobileSmall: '576px',
    mobileLarge: '768px',
    tablet: '992px',
    desktop: '1200px',
    wide: '1600px',
  },
  
  /**
   * Touch Targets (Minimum sizes for usability)
   */
  touchTarget: {
    minimum: '48px',
    comfortable: '56px',
    large: '64px',
  },
  
  /**
   * Component-Specific Tokens
   */
  components: {
    button: {
      height: {
        small: '36px',
        medium: '44px',
        large: '48px',
      },
      padding: {
        small: '0 16px',
        medium: '0 24px',
        large: '0 32px',
      },
    },
    
    input: {
      height: {
        small: '40px',
        medium: '48px',
        large: '56px',
      },
      padding: '12px 16px',
    },
    
    card: {
      padding: {
        small: '16px',
        medium: '24px',
        large: '32px',
      },
      borderWidth: '1px',
    },
    
    badge: {
      height: '24px',
      padding: '4px 12px',
      fontSize: '11px',
    },
    
    modal: {
      width: {
        small: '480px',
        medium: '640px',
        large: '920px',
      },
      maxHeight: '85vh',
    },
    
    blade: {
      width: {
        default: '480px',
        wide: '640px',
      },
    },
  },
} as Record<string, any>;

/**
 * Helper function to get color with opacity
 */
export const withOpacity = (color: string, opacity: number): string => {
  // Simple implementation for hex colors
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Theme mode type
 */
export type ThemeMode = 'light' | 'dark';

/**
 * Get background color by level and mode
 */
export const getBackgroundColor = (level: 0 | 1 | 2 | 3 | 4, mode: ThemeMode = 'light'): string => {
  const key = `level${level}${mode === 'light' ? 'Light' : 'Dark'}` as keyof typeof NobleTokens.colors.background;
  return NobleTokens.colors.background[key];
};

/**
 * Export individual token categories for convenience
 */
// Convenient exports for easier imports
export const Colors = NobleTokens.colors;
export const Typography = NobleTokens.typography;
export const Spacing = NobleTokens.spacing;
export const Shadows = NobleTokens.shadows;
export const Animation = NobleTokens.animation;
export const Breakpoints = NobleTokens.breakpoints;

// Aliased exports (for consistency with Phase 2 & 3 components)
export const NobleColors = NobleTokens.colors;
export const NobleTypography = NobleTokens.typography;
export const NobleSpacing = NobleTokens.spacing;
export const NobleShadows = NobleTokens.shadows;
export const NobleAnimation = NobleTokens.animation;
export const NobleBreakpoints = NobleTokens.breakpoints;

export default NobleTokens;

